import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { useRideStationContext } from "@ryde/redux";
import {
  canRemoveStationByProfileScope,
  isRideGreyed,
  isStationRouteWaypointStation,
  useHasPermissions,
  useProfileAuthorizationCheck,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { isRideStationDeleteAvailable } from "./ride-station-row-btns.utils";

export const useRideStationRowBtns = () => {
  const { t } = useTranslation();
  const {
    removeRideStation,
    rideDto,
    station,
    stationPassengers = [],
    mode,
  } = useRideStationContext();
  const { profileAuthorizationInCheck } = useProfileAuthorizationCheck();
  const { hasPermissions: hasCreateOtherSitePermission } = useHasPermissions({
    entity: "ride",
    action: "create_other_site",
  });

  const isGreyed = isRideGreyed(rideDto);

  const removeStationHandler = useEvent(async (): Promise<void> => {
    const result = await removeRideStation?.();

    if (result && !result.success) {
      warnAndToastSimpleResult(result, t);
    }
  });

  const isWaypoint = Boolean(station && isStationRouteWaypointStation(station));

  const deleteStationAvailable = useMemo(
    () => isRideStationDeleteAvailable({ rideDto, station, mode }),
    [mode, rideDto, station],
  );

  // DELETE UI: EVERY unit + EVERY site; create_other_site ⇒ unit only.
  // Waypoints have no passengers — always removable when delete is available.
  const canRemoveStationByScope = useMemo(
    () =>
      isWaypoint ||
      canRemoveStationByProfileScope({
        stationPassengers,
        profileAuthorizationInCheck,
        hasCreateOtherSite: hasCreateOtherSitePermission,
      }),
    [
      hasCreateOtherSitePermission,
      isWaypoint,
      profileAuthorizationInCheck,
      stationPassengers,
    ],
  );

  return {
    isGreyed,
    deleteStationAvailable,
    canRemoveStationByScope,
    removeStationHandler,
  };
};
